import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom'; // Removed BrowserRouter
import { Toaster } from 'react-hot-toast';

// Auth
import AuthContext from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';

// Layouts
import MainLayout from './layouts/MainLayout';
import AdminLayout from './layouts/AdminLayout';

// Public pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import WaitlistPage from './pages/WaitlistPage';
import AlumniVerificationPage from './pages/AlumniVerificationPage';

// School pages
import SchoolHomePage from './pages/school/HomePage';
import DepartmentsPage from './pages/school/DepartmentsPage';
import DepartmentDetailsPage from './pages/school/DepartmentDetailsPage';
import CoursesPage from './pages/school/CoursesPage';
import CourseEditPage from './pages/school/CourseEditPage';
import CourseDetailsPage from './pages/school/CourseDetailsPage';
import CourseSectionPage from './pages/school/CourseSectionPage';
import AddCoursePage from './pages/school/AddCoursePage';
import CourseSectionCreatePage from './pages/school/CourseSectionCreatePage';
import ProfessorsPage from './pages/school/ProfessorsPage';
import ProfessorCreatePage from './pages/school/ProfessorCreatePage';
import ProfessorDetailsPage from './pages/school/ProfessorDetailsPage';
import SearchResultsPage from './pages/school/SearchResultsPage';
import MaterialViewPage from './pages/school/MaterialViewPage';
import ProfessorEditPage from "./pages/school/ProfessorEditPage";
import CourseSectionEditPage from "./pages/school/CourseSectionEditPage";

// User pages
import ProfilePage from './pages/user/ProfilePage';
import SettingsPage from './pages/user/SettingsPage';
import MyCoursesPage from './pages/user/MyCoursesPage';
import MyMaterialsPage from './pages/user/MyMaterialsPage';
import PublicProfilePage from './pages/user/PublicProfilePage';

// Admin pages
import AdminDashboardPage from './pages/admin/DashboardPage';
import AdminUsersPage from './pages/admin/UsersPage';
import AdminSchoolsPage from './pages/admin/SchoolsPage';
import AdminMaterialsPage from './pages/admin/MaterialsPage';
import AdminReportsPage from './pages/admin/ReportsPage';
import AdminCoursesPage from './pages/admin/CoursesPage';
import AdminProfessorsPage from "./pages/admin/ProfessorsPage";
import AdminRequestsPage from './pages/admin/RequestsPage';
import AdminAuditLogsPage from './pages/admin/AuditLogsPage';
import AdminEmailBlastPage from './pages/admin/EmailBlastPage';

// Moderator pages
import ModeratorLayout from "./layouts/ModeratorLayout";
import ModeratorDashboardPage from "./pages/moderator/DashboardPage";
import ModeratorProfessorsPage from "./pages/moderator/ProfessorsPage";
import ModeratorCoursesPage from "./pages/moderator/CoursesPage";
import ModeratorReportsPage from "./pages/moderator/ReportsPage";
import ModeratorMaterialsPage from "./pages/moderator/MaterialsPage";
import ModeratorSchoolDetailsPage from "./pages/moderator/SchoolDetailsPage";
import ModeratorRequestsPage from "./pages/moderator/RequestsPage";

// Help page
import HelpPage from './pages/HelpPage';

// Account Status page
import AccountStatusPage from './pages/AccountStatusPage';

// API service
import AuthService from './services/AuthService';

// Theme context
import ThemeContext from './contexts/ThemeContext';
import { getThemePreference } from './utils/theme';

// Questions
import QuestionDetailPage from './pages/questions/QuestionDetailPage';
import QuestionEditPage from './pages/questions/QuestionEditPage';

// Notifications
import NotificationsPage from './pages/user/NotificationsPage';

// Chat
import ChatPage from './pages/ChatPage';

// Subscription
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCanceled from './pages/SubscriptionCanceled';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [theme, setTheme] = useState(() => getThemePreference());

  useEffect(() => {
    // Check if user is logged in
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (token) {
          const currentUser = await AuthService.getCurrentUser();
          setUser(currentUser);
          
          // Set theme from user preference if available
          if (currentUser?.themePreference) {
            setTheme(currentUser.themePreference);
          }
        }
      } catch (error) {
        console.error('Authentication check failed:', error);
        // Clear token if invalid
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);
  
  // Update theme when it changes
  // In App.jsx
  useEffect(() => {
  document.documentElement.setAttribute('data-theme', theme);
  
  // Make sure Chaos also adds the dark class
  if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
  
  localStorage.setItem('theme', theme);
}, [theme]);

  const login = (userData, token) => {
    localStorage.setItem('token', token);
    setUser(userData);
    
    // Set theme from user preference if available
    if (userData?.themePreference) {
      setTheme(userData.themePreference);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    // Reset theme to default
    setTheme('default');
  };

  const updateUser = (updatedUserData) => {
    setUser((prevUser) => ({
      ...prevUser,
      ...updatedUserData,
    }));
    
    // Update theme if preference changed
    if (updatedUserData?.themePreference) {
      setTheme(updatedUserData.themePreference);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">
      <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
    </div>;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser }}>
      <ThemeContext.Provider value={{ theme, setTheme }}>
        {/* Removed Router wrapper since it's already in index.js */}
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          <Route path="/verify-alumni" element={<AlumniVerificationPage />} />
          <Route path="/verify-alumni/:verificationCode" element={<AlumniVerificationPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/account-status" element={<AccountStatusPage />} />
          
          {/* Protected routes - require authentication */}
          <Route element={<PrivateRoute />}>
            {/* School routes */}
            <Route element={<MainLayout />}>
              <Route path="/school" element={<SchoolHomePage />} />
              <Route path="/departments" element={<DepartmentsPage />} />
              <Route path="/departments/:id" element={<DepartmentDetailsPage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/courses/new" element={
                user && (user.role === 'admin' || user.role === 'moderator') 
                  ? <AddCoursePage /> 
                  : <Navigate to="/courses" replace />
              } />
              <Route path="/courses/:id/edit" element={
                user && (user.role === 'admin' || user.role === 'moderator') 
                  ? <CourseEditPage /> 
                  : <Navigate to="/courses" replace />
              } />
              <Route path="/courses/:id" element={<CourseDetailsPage />} />
              <Route path="/courses/:courseId/sections/new" element={<CourseSectionCreatePage />} />
              <Route path="/courses/:courseId/sections/:sectionId" element={<CourseSectionPage />} />
              <Route path="/course-sections/new" element={
                user && (user.role === 'admin' || user.role === 'moderator') 
                  ? <CourseSectionCreatePage /> 
                  : <Navigate to="/courses" replace />
              } />
              <Route path="/courses/:courseId/sections/new" element={
                user && (user.role === 'admin' || user.role === 'moderator') 
                  ? <CourseSectionCreatePage /> 
                  : <Navigate to="/courses" replace />
              } />
              <Route path="/courses/:courseId/sections/:sectionId/edit" element={<CourseSectionEditPage />} />
              <Route path="/professors" element={<ProfessorsPage />} />
              <Route path="/professors/new" element={
                user && (user.role === 'admin' || user.role === 'moderator') 
                  ? <ProfessorCreatePage /> 
                  : <Navigate to="/professors" replace />
              } />
              <Route path="/professors/:id/edit" element={<ProfessorEditPage />} />
              <Route path="/professors/:id" element={<ProfessorDetailsPage />} />
              <Route path="/search" element={<SearchResultsPage />} />
              <Route path="/materials/:materialId" element={<MaterialViewPage />} />
              
              {/* User routes */}
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/my-courses" element={<MyCoursesPage />} />
              <Route path="/my-materials" element={<MyMaterialsPage />} />
              <Route path="/users/:userId" element={<PublicProfilePage />} />
              <Route path="/courses/:courseId/questions/:questionId" element={<QuestionDetailPage />} />
              <Route path="/courses/:courseId/questions/:questionId/edit" element={<QuestionEditPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/chat/:sessionId" element={<ChatPage />} />
              <Route path="/subscription" element={<SubscriptionPage />} />
              <Route path="/subscription/success" element={<SubscriptionSuccess />} />
              <Route path="/subscription/canceled" element={<SubscriptionCanceled />} />
            </Route>
            
            {/* Admin routes */}
            <Route 
              element={
                user && user.role === 'admin' 
                  ? <AdminLayout /> 
                  : <Navigate to="/school" replace />
              }
            >
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route path="/admin/schools" element={<AdminSchoolsPage />} />
              <Route path="/admin/courses" element={<AdminCoursesPage />} />
              <Route path="/admin/professors" element={<AdminProfessorsPage />} />
              <Route path="/admin/materials" element={<AdminMaterialsPage />} />
              <Route path="/admin/reports" element={<AdminReportsPage />} />
              <Route path="/admin/audit-logs" element={<AdminAuditLogsPage />} />
              <Route path="/admin/requests" element={<AdminRequestsPage />} />
              <Route path="/admin/email-blast" element={<AdminEmailBlastPage />} />
            </Route>
          </Route>

          {/* Moderator routes */}
          <Route path="/moderator" element={<ModeratorLayout />}>
            <Route index element={<ModeratorDashboardPage />} />
            <Route path="courses" element={<ModeratorCoursesPage />} />
            <Route path="professors" element={<ModeratorProfessorsPage />} />
            <Route path="reports" element={<ModeratorReportsPage />} />
            <Route path="materials" element={<ModeratorMaterialsPage />} />
            <Route path="school" element={<ModeratorSchoolDetailsPage />} />
            <Route path="requests" element={<ModeratorRequestsPage />} />  
          </Route>
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        
        {/* Toast notifications */}
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              borderRadius: '10px',
              background: theme === 'dark' ? '#333' : '#fff',
              color: theme === 'dark' ? '#fff' : '#333',
            },
          }}
        />
      </ThemeContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;